import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getApiBaseUrl } from '../../../utils/apiBase';

const isPdfUrl = (url = '', mimeType = '') =>
  mimeType === 'application/pdf' || /\.pdf($|\?|#)/i.test(url);

const isWordUrl = (url = '', mimeType = '') =>
  mimeType.includes('word') || /\.(doc|docx)($|\?|#)/i.test(url);

const buildUploadedPreviewUrl = (data) => {
  if (data?.previewToken) {
    return `${getApiBaseUrl()}/vlt/preview/document/${data.previewToken}`;
  }
  if (data?.previewUrl) {
    return data.previewUrl;
  }
  return '';
};

const VltDocumentViewer = ({ show, vlt, onHide }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [mimeType, setMimeType] = useState('');

  useEffect(() => {
    if (!show || !vlt?.id) {
      setPreviewUrl('');
      setMimeType('');
      return;
    }

    const loadPreview = async () => {
      try {
        setLoading(true);
        const response = await api.vlt.getDocumentPreviewToken(vlt.id);
        const data = response.data?.data;

        if (!data) {
          toast.error('Unable to load document preview');
          onHide();
          return;
        }

        if (data.isExternal) {
          window.open(data.previewUrl, '_blank', 'noopener,noreferrer');
          onHide();
          return;
        }

        const uploadedPreviewUrl = buildUploadedPreviewUrl(data);
        if (!uploadedPreviewUrl) {
          toast.error('Unable to load document preview');
          onHide();
          return;
        }

        setPreviewUrl(uploadedPreviewUrl);
        setMimeType(data.mimeType || '');
      } catch (error) {
        console.error('Error loading document preview:', error);
        toast.error(error.response?.data?.message || 'Failed to load document');
        onHide();
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [show, vlt?.id, onHide]);

  const viewerSrc = useMemo(() => {
    if (!previewUrl) return '';

    if (isPdfUrl(previewUrl, mimeType)) {
      return previewUrl;
    }

    if (isWordUrl(previewUrl, mimeType)) {
      return previewUrl;
    }

    return previewUrl;
  }, [previewUrl, mimeType]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
        <Modal.Title className="text-base font-bold">{vlt?.title || 'Document Viewer'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 bg-gray-100" style={{ minHeight: '70vh' }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: '70vh' }}>
            <Spinner animation="border" className="text-purple-600" />
          </div>
        ) : viewerSrc ? (
          <iframe
            title={vlt?.title || 'VLT Document'}
            src={viewerSrc}
            className="w-100 border-0"
            style={{ minHeight: '70vh' }}
          />
        ) : (
          <div className="text-center py-5 text-gray-600">Document preview is not available.</div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default VltDocumentViewer;
